import React, { useRef } from "react";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { darkBg, gridColor, labelColor, makeGradient, tooltipStyle } from "../api/echartsTheme";
import GraphDownloadButton from "../../Global/other/GraphDownloadButton";
import { formatSAST } from "../../Global/other/formatSAST";

const color = "#FDB940";

export default function DeviceSwitchingGraph({ graph_data }) {
  const chartRef = useRef(null);

  if (!graph_data) {
    return (
      <Grid item sx={{ p: 2 }}>
        <CircularProgress size={20} sx={{ color }} />
      </Grid>
    );
  }

  const seriesData = (graph_data.timestamps || []).map((ts, i) => [
    new Date(ts).getTime(),
    graph_data.unique_meter_count[i] ?? null,
  ]);

  const option = {
    backgroundColor: darkBg,
    animation: false,
    grid: { top: 16, right: 24, bottom: 76, left: 56 },
    dataZoom: [
      { type: "inside", xAxisIndex: 0, filterMode: "filter" },
      {
        type: "slider",
        xAxisIndex: 0,
        height: 18,
        bottom: 32,
        backgroundColor: "rgba(255,255,255,0.03)",
        borderColor: "rgba(255,255,255,0.07)",
        fillerColor: "rgba(253,185,64,0.2)",
        handleStyle: { color: "#FDB940", borderColor: "#FDB940" },
        moveHandleStyle: { color: "#FDB940" },
        emphasis: { handleStyle: { color: "#FDB940" } },
        textStyle: { color: "transparent" },
        brushSelect: false,
        showDetail: false,
      },
    ],
    tooltip: {
      trigger: "axis",
      ...tooltipStyle,
      formatter: (params) =>
        params
          .filter((p) => p.value !== null)
          .map((p) => `${p.seriesName}: <b>${p.value[1]} devices</b>`)
          .join("<br/>"),
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { lineStyle: { color: gridColor } },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        formatter: (v) => {
          const d = new Date(v);
          return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
        },
      },
      splitLine: { show: false },
    },
    legend: {
      bottom: 4,
      left: 16,
      backgroundColor: "rgba(255,255,255,0.06)",
      borderRadius: 20,
      padding: [5, 14],
      textStyle: { color: "#e0e6f0", fontSize: 11 },
      itemWidth: 16,
      itemHeight: 3,
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: labelColor, fontSize: 10 },
      splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
    },
    series: [
      {
        name: "Meters Controlled Per Hour",
        type: "line",
        smooth: true,
        symbol: "none",
        lineStyle: { color, width: 2 },
        areaStyle: { color: makeGradient(color), opacity: 0.4 },
        itemStyle: { color },
        data: seriesData.filter(([, y]) => y !== null),
      },
    ],
  };

  const handleDownloadCsv = () => {
    if (!graph_data?.timestamps) return;
    let csv = "Timestamp (SAST),Devices Controlled\r\n";
    graph_data.timestamps.forEach((ts, i) => {
      csv += `${formatSAST(ts)},${graph_data.unique_meter_count[i] ?? ""}\r\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Device_Switching_${dayjs().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPng = () => {
    const url = chartRef.current?.getEchartsInstance()?.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: darkBg });
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `Device_Switching_${dayjs().format("YYYY-MM-DD")}.png`;
    a.click();
  };

  return (
    <Card sx={{ borderRadius: "12px", backgroundColor: darkBg, border: "1px solid rgba(255,255,255,0.06)" }}>
      <Grid container alignItems="center" sx={{ pt: "20px", px: "20px", pb: "12px" }}>
        <Grid item xs>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.01em" }}>
            Device Switching Data
          </Typography>
        </Grid>
        <Grid item sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#FDB940", display: "inline" }}>
              {graph_data.connected_device_count} / {graph_data.active_device_count}
            </Typography>
            <Typography component="span" sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.45)", ml: "6px" }}>
              connected now
            </Typography>
          </Box>
          <GraphDownloadButton onDownloadCsv={handleDownloadCsv} onDownloadPng={handleDownloadPng} />
        </Grid>
      </Grid>
      <Grid item sx={{ width: "100%", px: "12px", pb: "12px" }}>
        <ReactECharts ref={chartRef} option={option} style={{ height: 380 }} notMerge />
      </Grid>
    </Card>
  );
}
